import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BiChevronDown, BiX } from 'react-icons/bi';
import { useLocation, useNavigate } from 'react-router-dom';

import { useRoles } from '@/hooks/useRoles';
import { usePermissions } from '@/permissions/usePermissions';
import { getVisibleNavGroups, type NavGroupView } from '@/router/routes.config';
import { MIN_SEARCH_LENGTH } from '@/store/global-search/global-search.api';
import { useGlobalSearchStore } from '@/store/global-search/global-search.store';
import { useVessels } from '@/store/vessels/vessels.store';
import { findMatchRange } from '@/utils/match-range';
import { UserRole } from '@/utils/roles';
import { useIsMobile } from '@hooks/useIsMobile';

import { Avatar } from '../Avatar/Avatar';

import s from './GlobalSearch.module.scss';
import { isSearchHotkey } from './hotkey';
import { useGlobalSearchResults } from './useGlobalSearchResults';

/** One row in the results list. */
type ResultItem = {
  id: string;
  label: string;
  subtitle?: string;
  icon?: React.ReactNode;
  hint?: string;
  /** Query to mark inside the label, where the row's match is its name. */
  highlight?: string;
  run: () => void;
};

/** The label with the first match of `query` marked, case and accents aside. */
const MarkedLabel = ({ label, query }: { label: string; query?: string }) => {
  const range = query ? findMatchRange(label, query) : null;
  if (!range) return <>{label}</>;
  return (
    <>
      {label.slice(0, range.start)}
      <mark className={s.match}>{label.slice(range.start, range.end)}</mark>
      {label.slice(range.end)}
    </>
  );
};

type ResultGroup = {
  /** Distinct from `heading` — a backend category and a nav group can share a name. */
  id: string;
  heading: string;
  items: ResultItem[];
};

const ALL_SCOPE = 'all';

/** Longest match wins, so /documents/<id>.gallery scopes to Gallery, not Documents. */
const scopeForPath = (groups: NavGroupView[], pathname: string): string => {
  let best: { key: string; length: number } | null = null;
  for (const group of groups) {
    for (const item of group.items) {
      const onRoute = pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (onRoute && (!best || item.to.length > best.length)) {
        best = { key: group.key, length: item.to.length };
      }
    }
  }
  return best?.key ?? ALL_SCOPE;
};

/**
 * Global search, behind the header search icon and Cmd/Ctrl-K.
 *
 * Always mounted so the key binding works on every screen; the panel itself is
 * a separate component that mounts on open, which is what makes every open a
 * fresh search — an inherited query or a stale scope would silently cut results
 * the user never asked to narrow, and mounting handles that without an effect
 * that resets four pieces of state.
 */
export const GlobalSearch = () => {
  const isOpen = useGlobalSearchStore((st) => st.isOpen);
  const toggle = useGlobalSearchStore((st) => st.toggle);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isSearchHotkey(e)) return;
      // preventDefault because Firefox gives Ctrl-K to the address bar.
      e.preventDefault();
      toggle();
    };
    // Capture phase, so a dialog or editor that stops keydown inside its own
    // subtree cannot swallow an app-wide shortcut before it reaches us.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [toggle]);

  if (!isOpen) return null;
  return <GlobalSearchPanel />;
};

/**
 * The panel. Records come from the seven backend `/search` endpoints via
 * useGlobalSearchResults; pages come from the same permission-filtered nav
 * config the sidebar renders, so this can never offer a destination the user is
 * not allowed to open.
 */
const GlobalSearchPanel = () => {
  const close = useGlobalSearchStore((st) => st.close);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isMobile = useIsMobile();

  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<string>(ALL_SCOPE);
  const [scopeMenuOpen, setScopeMenuOpen] = useState(false);
  /**
   * The highlight, tied to the search it belongs to. Results shrink and reorder
   * as the query and scope change, so the index is only meaningful for the
   * (query, scope) it was chosen under — deriving that beats resetting it from
   * an effect after the list has already rendered with a stale highlight.
   */
  const [selection, setSelection] = useState({ key: '', index: 0 });

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { primaryRole, userRoles } = useRoles();
  const { can, canAny } = usePermissions();
  const { vessel } = useVessels();

  const navGroups = useMemo(
    () =>
      getVisibleNavGroups({
        isServiceProvider: userRoles?.includes(UserRole.SERVICE_PROVIDER) ?? false,
        isGuest: primaryRole === UserRole.GUEST,
        isMobile,
        can,
        canAny,
        vesselId: vessel?.id,
      }),
    [userRoles, primaryRole, isMobile, can, canAny, vessel?.id],
  );

  /** Only for the "Current" marker in the menu — the default scope is All. */
  const currentScope = scopeForPath(navGroups, pathname);

  useEffect(() => {
    // Focus once the portal is in the document. preventScroll, or iOS scrolls the page as
    // the keyboard opens and the sheet's search bar slides up under the camera.
    const raf = requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const restoreFocusTo = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      restoreFocusTo?.focus?.();
    };
  }, []);

  const scopes = useMemo(
    () => [
      { key: ALL_SCOPE, label: 'All' },
      ...navGroups.map((g) => ({ key: g.key, label: g.label })),
    ],
    [navGroups],
  );
  const scopeLabel = scopes.find((sc) => sc.key === scope)?.label ?? 'All';

  const trimmedQuery = query.trim();
  const { hits, isFetching, isError, failed, isSettled } = useGlobalSearchResults(query, true);

  /** Records from the backends, grouped by category in registry order. */
  const recordGroups = useMemo<ResultGroup[]>(() => {
    const byCategory = new Map<string, ResultItem[]>();
    for (const hit of hits) {
      if (scope !== ALL_SCOPE && hit.section !== scope) continue;
      const items = byCategory.get(hit.category) ?? [];
      const { person } = hit;
      items.push({
        id: hit.id,
        label: hit.label,
        subtitle: hit.subtitle,
        ...(person && {
          icon: (
            <Avatar
              src={person.avatar}
              firstName={person.firstName}
              lastName={person.lastName}
              size={28}
              alt=""
            />
          ),
          hint: 'Contact',
          highlight: trimmedQuery,
        }),
        run: () => {
          navigate(hit.to);
          close();
        },
      });
      byCategory.set(hit.category, items);
    }
    return [...byCategory].map(([heading, items]) => ({ id: `hit-${heading}`, heading, items }));
  }, [hits, scope, trimmedQuery, navigate, close]);

  /** Destinations, from the nav config. Always available, never a request. */
  const pageGroups = useMemo<ResultGroup[]>(() => {
    const q = trimmedQuery.toLowerCase();
    const matches = (label: string) => q === '' || label.toLowerCase().includes(q);

    return navGroups
      .filter((group) => scope === ALL_SCOPE || group.key === scope)
      .map((group) => ({
        id: `page-${group.key}`,
        heading: group.label,
        // A group whose own name matches keeps all its rows: searching
        // "maintain" is a question about the section, not about one page in it.
        items: group.items
          .filter((item) => matches(item.label) || matches(group.label))
          .map((item) => ({
            id: `page-${group.key}-${item.key}`,
            label: item.label,
            icon: item.icon,
            hint: 'Page',
            run: () => {
              navigate(item.to);
              close();
            },
          })),
      }))
      .filter((group) => group.items.length > 0);
  }, [trimmedQuery, scope, navGroups, navigate, close]);

  // Records first: with a query typed, the answer is a record far more often
  // than it is a screen. Pages stay below as the wayfinding fallback.
  const groups = useMemo(() => [...recordGroups, ...pageGroups], [recordGroups, pageGroups]);
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const selectionKey = `${trimmedQuery}|${scope}`;
  const rawIndex = selection.key === selectionKey ? selection.index : 0;
  // Results also shrink while the key holds still (a slower service landing can
  // only add, but a scope filter applied to arriving hits can remove), so the
  // highlight must never point past the end.
  const selected = Math.min(rawIndex, Math.max(flat.length - 1, 0));
  const select = (index: number) => setSelection({ key: selectionKey, index });

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${selected}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      select(Math.min(selected + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      select(Math.max(selected - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // On a touch screen the on-screen keyboard covers most of the results and the key reads
      // "search", so it closes the keyboard; a result is opened by tapping it.
      if (window.matchMedia('(pointer: coarse)').matches) inputRef.current?.blur();
      else flat[selected]?.run();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Escape closes the innermost thing first: the scope menu, then the panel.
      if (scopeMenuOpen) setScopeMenuOpen(false);
      else close();
    }
  };

  let rowIndex = -1;

  return createPortal(
    <div className={s.overlay} onClick={close}>
      <div className={s.panel} onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <div className={s.searchBar}>
          <div className={s.scope}>
            <button
              type="button"
              className={s.scopeButton}
              aria-haspopup="listbox"
              aria-expanded={scopeMenuOpen}
              aria-label={`Search Scope: ${scopeLabel}`}
              onClick={() => setScopeMenuOpen((v) => !v)}
            >
              <span className={s.scopeLabelText}>{scopeLabel}</span>
              <BiChevronDown className={scopeMenuOpen ? s.chevronOpen : undefined} />
            </button>
            {scopeMenuOpen && (
              <ul className={s.scopeMenu} role="listbox" aria-label="Search Scope">
                {scopes.map((sc) => (
                  <li key={sc.key}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={sc.key === scope}
                      className={`${s.scopeOption} ${sc.key === scope ? s.scopeOptionActive : ''}`}
                      onClick={() => {
                        setScope(sc.key);
                        setScopeMenuOpen(false);
                        inputRef.current?.focus();
                      }}
                    >
                      <span>{sc.label}</span>
                      {sc.key === currentScope && sc.key !== ALL_SCOPE && (
                        <span className={s.scopeHint}>Current</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <input
            ref={inputRef}
            className={s.input}
            type="text"
            enterKeyHint="search"
            role="combobox"
            aria-expanded={flat.length > 0}
            aria-controls="global-search-listbox"
            aria-activedescendant={flat.length > 0 ? `global-search-option-${selected}` : undefined}
            aria-label={scope === ALL_SCOPE ? 'Search VesselHaven' : `Search in ${scopeLabel}`}
            placeholder={scope === ALL_SCOPE ? 'Search' : `Search in ${scopeLabel}`}
            value={query}
            onFocus={() => setScopeMenuOpen(false)}
            onChange={(e) => setQuery(e.target.value)}
          />
          {/* Phones only: the sheet fills the screen, so there is no backdrop to tap and
              usually no Escape key. */}
          <button type="button" className={s.close} aria-label="Close Search" onClick={close}>
            <BiX aria-hidden="true" />
          </button>
        </div>

        <div
          ref={listRef}
          id="global-search-listbox"
          role="listbox"
          aria-label="Search Results"
          className={s.results}
        >
          {flat.length === 0 && (
            <div className={s.empty}>
              {/* Name the cut that is doing the hiding, so the user knows
                  whether to change the words, wait, or widen the scope. */}
              {isFetching || !isSettled
                ? 'Searching…'
                : isError
                  ? 'Search is unavailable right now. Please try again in a moment.'
                  : trimmedQuery.length > 0 && trimmedQuery.length < MIN_SEARCH_LENGTH
                    ? `Please type at least ${MIN_SEARCH_LENGTH} characters to search your data.`
                    : scope === ALL_SCOPE
                      ? 'No results match. Please try different words.'
                      : `No matches in ${scopeLabel}. Please switch to All to search everything.`}
            </div>
          )}
          {/* A partial answer is still an answer — but say so, or a crew member
              whose wallet rights are missing reads a short list as the truth. */}
          {failed.length > 0 && flat.length > 0 && (
            <div className={s.notice}>Some results could not be loaded ({failed.join(', ')}).</div>
          )}
          {groups.map((group) => (
            <div key={group.id} className={s.group}>
              <div className={s.groupHeading}>{group.heading}</div>
              {group.items.map((item) => {
                rowIndex += 1;
                const index = rowIndex;
                return (
                  <div
                    key={item.id}
                    id={`global-search-option-${index}`}
                    data-index={index}
                    role="option"
                    aria-selected={index === selected}
                    className={`${s.row} ${index === selected ? s.rowSelected : ''}`}
                    onMouseEnter={() => select(index)}
                    onClick={item.run}
                  >
                    {item.icon && (
                      <span className={s.rowIcon} aria-hidden="true">
                        {item.icon}
                      </span>
                    )}
                    <span className={s.rowText}>
                      <span className={s.rowLabel}>
                        <MarkedLabel label={item.label} query={item.highlight} />
                      </span>
                      {item.subtitle && <span className={s.rowSubtitle}>{item.subtitle}</span>}
                    </span>
                    {item.hint && <span className={s.rowHint}>{item.hint}</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className={s.footer}>↑↓ to navigate · ↵ to open · esc to close</div>
      </div>
    </div>,
    document.body,
  );
};

export default GlobalSearch;
